import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  postEngagementTimeTrackingMilestoneRequest,
  selectMaybeClient,
  selectSession,
} from '@client/model';

interface UseOnboardingTimeTrackerOptions {
  thresholdMinutes?: number;
}

export function useOnboardingTimeTracker({
  thresholdMinutes = 10,
}: UseOnboardingTimeTrackerOptions) {
  const location = useLocation();
  const dispatch = useDispatch();

  const client = useSelector(selectMaybeClient);
  const session = useSelector(selectSession);

  const totalTimeMapRef = useRef<Record<string, number>>({});
  const thresholdHitMapRef = useRef<Record<string, boolean>>({});
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (!client) return;

    const clientId = client.clientId;
    if (!clientId) return;

    const path = location.pathname;
    const isOnboarding = /\/clients\/[^/]+\/onboarding(\/.*)?/.test(path);
    if (!isOnboarding) return;

    // Start counting when user enters onboarding route
    if (!totalTimeMapRef.current[clientId]) totalTimeMapRef.current[clientId] = 0;
    if (!thresholdHitMapRef.current[clientId]) thresholdHitMapRef.current[clientId] = false;

    startTimeRef.current = Date.now();

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (startTimeRef.current) {
          const duration = (Date.now() - startTimeRef.current) / 1000;
          totalTimeMapRef.current[clientId] += duration;
          startTimeRef.current = null;
        }
      } else {
        startTimeRef.current = Date.now();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const intervalId = setInterval(() => {
      if (!startTimeRef.current) return;

      const currentDuration = (Date.now() - startTimeRef.current) / 1000;
      const total = totalTimeMapRef.current[clientId] + currentDuration;

      const thresholdSec = thresholdMinutes * 60;
      if (!thresholdHitMapRef.current[clientId] && total >= thresholdSec) {
        thresholdHitMapRef.current[clientId] = true;

        dispatch(
          postEngagementTimeTrackingMilestoneRequest({
            userType: session.advisor ? 'advisor' : 'client',
            clientId: clientId,
            advisorId: client.advisorId,
          }),
        );
      }
    }, 1000);

    return () => {
      // update total for this client when leaving route
      if (startTimeRef.current) {
        const duration = (Date.now() - startTimeRef.current) / 1000;
        totalTimeMapRef.current[clientId] += duration;
      }
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [location, dispatch, client, session, thresholdMinutes]);

  const clientId = client?.clientId;
  const totalTime = clientId ? totalTimeMapRef.current[clientId] || 0 : 0;
  const thresholdHit = clientId ? thresholdHitMapRef.current[clientId] || false : false;

  return { totalTime, thresholdHit };
}
